import React, { Component } from 'react';
import { Plus, Edit, Trash2, Eye, EyeOff, X, Tag } from 'lucide-react';
import apiService from '@/services/api';
import { PostCreateForm } from '@/components/SocialFeed/PostCreateForm';
import { PostPreview } from '@/components/SocialFeed/PostPreview';
import styles from './PostManagement.module.scss';

interface Post {
  id: string;
  title?: string;
  description?: string;
  contentText?: string;
  postType: string;
  attachments?: any[];
  status: 'draft' | 'published';
  viewsCount: number;
  likesCount: number;
  commentsCount: number;
  sharesCount: number;
  createdAt: string;
  publishedAt?: string;
  tags?: string[];
}

interface PostManagementState {
  posts: Post[];
  loading: boolean;
  showCreateForm: boolean;
  editingPost: Post | null;
  previewingPost: Post | null;
  currentPage: number;
  hasMore: boolean;
  error: string | null;
}

export class PostManagement extends Component<{}, PostManagementState> {
  constructor(props: {}) {
    super(props);
    this.state = {
      posts: [],
      loading: true,
      showCreateForm: false,
      editingPost: null,
      previewingPost: null,
      currentPage: 1,
      hasMore: true,
      error: null,
    };
  }

  componentDidMount() {
    this.loadPosts();
  }

  loadPosts = async (page: number = 1) => {
    this.setState({ loading: page === 1, error: null });

    try {
      // First, get the current user's profile to get the profile ID
      const profileResponse = await apiService.getCustomerProfile();
      
      if (!profileResponse.success || !profileResponse.data) {
        throw new Error('Failed to load user profile');
      }

      const profile = profileResponse.data;
      // The profile ID is in the 'id' field
      const customerId = profile.id;

      if (!customerId) {
        throw new Error('User profile ID not found');
      }

      console.log('[PostManagement] Loading posts for customer ID:', customerId);

      // Now fetch the posts for this customer
      const response = await apiService.getCustomerPosts(customerId, page, 20);

      console.log('[PostManagement] Posts response:', response);

      if (response.success && response.data) {
        const newPosts = response.data.posts || [];
        const pagination = response.data.pagination || {};

        console.log('[PostManagement] Loaded posts:', newPosts.length);

        this.setState({
          posts: page === 1 ? newPosts : [...this.state.posts, ...newPosts],
          hasMore: pagination.hasMore !== false,
          currentPage: page,
          loading: false,
        });
      } else {
        throw new Error(response.message || 'Failed to load posts');
      }
    } catch (error: any) {
      console.error('[PostManagement] Failed to load posts:', error);
      this.setState({
        error: error.message || 'Failed to load posts',
        loading: false,
      });
    }
  };

  handleCreatePost = async (data: {
    title?: string;
    description?: string;
    contentText?: string;
    status: 'draft' | 'published';
    files: File[];
    tags?: string[];
  }) => {
    try {
      const response = await apiService.createPost(data);

      if (response.success) {
        this.setState({ showCreateForm: false });
        this.loadPosts(1);
        
        // If published, show success message
        if (data.status === 'published') {
          alert('Post published successfully!');
        } else {
          alert('Post saved as draft!');
        }
      } else {
        throw new Error(response.message || 'Failed to create post');
      }
    } catch (error: any) {
      console.error('Failed to create post:', error);
      alert(error.message || 'Failed to create post. Please try again.');
      throw error;
    }
  };

  handleUpdatePost = async (postId: string, data: {
    title?: string;
    description?: string;
    contentText?: string;
    status?: 'draft' | 'published';
    tags?: string[];
  }) => {
    try {
      const response = await apiService.updatePost(postId, data);

      if (response.success) {
        this.setState({ editingPost: null });
        this.loadPosts(1);
        alert('Post updated successfully!');
      } else {
        throw new Error(response.message || 'Failed to update post');
      }
    } catch (error: any) {
      console.error('Failed to update post:', error);
      alert(error.message || 'Failed to update post. Please try again.');
      throw error;
    }
  };

  handleDeletePost = async (postId: string) => {
    if (!window.confirm('Are you sure you want to delete this post? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await apiService.deletePost(postId);

      if (response.success) {
        this.setState({
          posts: this.state.posts.filter(p => p.id !== postId),
        });
        alert('Post deleted successfully!');
      } else {
        throw new Error(response.message || 'Failed to delete post');
      }
    } catch (error: any) {
      console.error('Failed to delete post:', error);
      alert(error.message || 'Failed to delete post. Please try again.');
    }
  };

  handlePublishPost = async (postId: string) => {
    try {
      const response = await apiService.publishPost(postId);

      if (response.success) {
        this.loadPosts(1);
        alert('Post published successfully!');
      } else {
        throw new Error(response.message || 'Failed to publish post');
      }
    } catch (error: any) {
      console.error('Failed to publish post:', error);
      alert(error.message || 'Failed to publish post. Please try again.');
    }
  };

  handlePreviewPost = async (postId: string) => {
    try {
      const response = await apiService.previewPost(postId);

      if (response.success && response.data) {
        this.setState({ previewingPost: response.data });
      } else {
        throw new Error(response.message || 'Failed to preview post');
      }
    } catch (error: any) {
      console.error('Failed to preview post:', error);
      alert(error.message || 'Failed to preview post. Please try again.');
    }
  };

  formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  render() {
    const {
      posts,
      loading,
      showCreateForm,
      editingPost,
      previewingPost,
      error,
    } = this.state;

    return (
      <div className={styles.postManagementContainer}>
        <div className={styles.header}>
          <h1 className={styles.title}>My Posts</h1>
          <div className={styles.headerButtons}>
            <button
              className={styles.goToPostsButton}
              onClick={() => {
                window.location.href = '/customer/friends';
              }}
            >
              <span>Go to Posts</span>
            </button>
            <button
              className={styles.createButton}
              onClick={() => this.setState({ showCreateForm: true, editingPost: null })}
            >
              <Plus size={20} />
              <span>Create Post</span>
            </button>
          </div>
        </div>

        {error && (
          <div className={styles.errorMessage}>
            <p>{error}</p>
            <button onClick={() => this.loadPosts(1)} className={styles.retryButton}>
              Retry
            </button>
          </div>
        )}

        {/* Create/Edit Form */}
        {(showCreateForm || editingPost) && (
          <div className={styles.formContainer}>
            <PostCreateForm
              onSubmit={editingPost
                ? (data) => this.handleUpdatePost(editingPost.id, data)
                : this.handleCreatePost}
              onCancel={() => this.setState({ showCreateForm: false, editingPost: null })}
              initialData={editingPost ? {
                title: editingPost.title,
                description: editingPost.description,
                contentText: editingPost.contentText,
                status: editingPost.status,
                tags: editingPost.tags,
              } : undefined}
              isEditing={!!editingPost}
            />
          </div>
        )}

        {/* Posts List */}
        {loading && posts.length === 0 ? (
          <div className={styles.loadingContainer}>
            <div className={styles.spinner}></div>
            <p>Loading your posts...</p>
          </div>
        ) : posts.length === 0 ? (
          <div className={styles.emptyState}>
            <p>You haven't created any posts yet.</p>
            <button
              className={styles.createFirstButton}
              onClick={() => this.setState({ showCreateForm: true })}
            >
              Create Your First Post
            </button>
          </div>
        ) : (
          <div className={styles.postsList}>
            {posts.map((post) => (
              <div key={post.id} className={styles.postItem}>
                <div className={styles.postHeader}>
                  <div className={styles.postInfo}>
                    <h3 className={styles.postTitle}>
                      {post.title || 'Untitled Post'}
                    </h3>
                    <div className={styles.postMeta}>
                      <span className={styles.postDate}>
                        {post.publishedAt
                          ? `Published: ${this.formatDate(post.publishedAt)}`
                          : `Created: ${this.formatDate(post.createdAt)}`}
                      </span>
                      <span className={`${styles.statusBadge} ${styles[post.status]}`}>
                        {post.status === 'draft' ? 'Draft' : 'Published'}
                      </span>
                    </div>
                  </div>
                  <div className={styles.postStats}>
                    <span>👁️ {post.viewsCount}</span>
                    <span>❤️ {post.likesCount}</span>
                    <span>💬 {post.commentsCount}</span>
                    <span>📤 {post.sharesCount}</span>
                  </div>
                </div>

                {post.description && (
                  <p className={styles.postDescription}>{post.description}</p>
                )}

                {post.contentText && (
                  <p className={styles.postContent}>
                    {post.contentText.length > 150
                      ? post.contentText.substring(0, 150) + '...'
                      : post.contentText}
                  </p>
                )}

                {/* Tags */}
                {post.tags && post.tags.length > 0 && (
                  <div className={styles.postTags}>
                    <Tag size={16} className={styles.tagsIcon} />
                    <div className={styles.tagsList}>
                      {post.tags.map((tag, index) => (
                        <span key={index} className={styles.tag}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className={styles.postActions}>
                  <button
                    className={styles.actionButton}
                    onClick={() => this.handlePreviewPost(post.id)}
                    title="Preview post"
                  >
                    <Eye size={18} />
                    <span>Preview</span>
                  </button>
                  <button
                    className={styles.actionButton}
                    onClick={() => this.setState({ editingPost: post, showCreateForm: false })}
                    title="Edit post"
                  >
                    <Edit size={18} />
                    <span>Edit</span>
                  </button>
                  {post.status === 'draft' && (
                    <button
                      className={styles.actionButton}
                      onClick={() => this.handlePublishPost(post.id)}
                      title="Publish post"
                    >
                      <EyeOff size={18} />
                      <span>Publish</span>
                    </button>
                  )}
                  <button
                    className={`${styles.actionButton} ${styles.deleteButton}`}
                    onClick={() => this.handleDeletePost(post.id)}
                    title="Delete post"
                  >
                    <Trash2 size={18} />
                    <span>Delete</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Preview Modal */}
        {previewingPost && (
          <PostPreview
            post={previewingPost}
            onClose={() => this.setState({ previewingPost: null })}
            onPublish={() => {
              if (previewingPost.status === 'draft') {
                this.handlePublishPost(previewingPost.id);
              }
              this.setState({ previewingPost: null });
            }}
          />
        )}
      </div>
    );
  }
}

export default PostManagement;

